import { useState, useEffect, useRef, useMemo } from 'react';
import Chart from 'chart.js/auto';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import VerticalBarChart from '../components/charts/VerticalBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { parseCost, exportCSV, formatSavingStr, buildExportFilename, ADD_NEW_ACCOUNT, setScopedItem, STORAGE_KEYS } from '../components/common/Helper';
import { RecCell, RecommendedCostCell , SavingCell } from '../components/optimization/RecCells.jsx';

import OptimizationService from '../services/OptimizationService';
import MultiSelect from '../components/common/MultiSelect.jsx';
import useTagList from '../hooks/useTagList';
import { useAuth } from '../state/AuthContext';

// ── Row Mapper ─────────────────────────────────────────────
const mapEIPRows = (data) =>
  data.map((item, index) => {
    const allocationId = item.AllocationId ?? item.allocationId ?? `eipalloc-${index}`;
    const accountId   = item.AccountId ?? item.accountId ?? '—';
    const publicIp    = item.PublicIp ?? item.publicIp ?? '—';
    const region      = item.Region ?? item.region ?? '—';
    const current     = parseCost(item.CurrentOnDemandMonthlyPriceValue ?? item.currentCost ?? 0);
    const recommended = parseCost(item.CustomRecommendedOnDemandMonthlyPriceValue ?? item.recommendedCost ?? 0);

    // Use status + associatedWith directly from API when available
    const apiStatus       = item.status ?? item.Status ?? '';
    const apiAssociated   = item.associatedWith ?? item.AssociatedWith ?? '';
    const instanceId      = item.InstanceId ?? item.instanceId ?? '';
    const associationId   = item.AssociationId ?? item.associationId ?? '';

    // Determine display status
    const statusRaw = apiStatus || ((!instanceId && !associationId) ? 'Unattached' : 'Attached');
    const statusLower = statusRaw.toLowerCase();
    let status = statusRaw, statusClass = 'st-attached', statusOrder = 2;
    if (statusLower.includes('unattach') || statusLower.includes('unused')) { statusClass = 'st-unattached'; statusOrder = 0; }
    else if (statusLower.includes('stop')) { statusClass = 'st-warn'; statusOrder = 1; }

    // associatedWith: prefer API field, then instance id, then association id
    const associatedWith = apiAssociated || instanceId || (associationId ? `eipassoc-${associationId.slice(-8)}` : '—');

    // Recommendation keys
    const rawRecType  = item.recommendationType ?? item.RecommendationType ?? '';
    const displayVal  = rawRecType;
    const t           = displayVal.toUpperCase().trim();
    const rawRemark   = item.CustomRecomendationRemark ?? item.customRecomendationRemark ?? item.customRecommendationRemark ?? '';
    const remarkClean = rawRemark.toUpperCase().trim() === 'NA' ? '' : rawRemark.trim();
    const rawDetail   = item.recommendationDetail ?? item.RecommendationDetail ?? '';
    const detailClean = rawDetail.toUpperCase().trim() === 'NA' ? '' : rawDetail.trim();
    const recommendationRemark = remarkClean || detailClean;

    let recType = displayVal || 'OPTIMAL', recPillClass = 'rec-rs', recLabel = '';
    if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
      recType = displayVal || 'OPTIMAL';
      recPillClass = 'rec-ok';
    } else if (t === 'RELEASE' || t === 'UNATTACHED' || t === 'DELETE') {
      recPillClass = 'rec-id';
    } else if (t === 'STOP' || t === 'STOPPED' || t === 'SCHEDULE') {
      recPillClass = 'rec-warn';
    }

    const saving = parseCost(item.savingCost ?? item.SavingCost) || (current - recommended);

    const tagsRaw = item.Tags ?? item.tags;
    const tagsArr = Array.isArray(tagsRaw) && tagsRaw.length > 0
      ? tagsRaw.map((t) => typeof t === 'object' ? { key: t.Key ?? t.key ?? '', value: t.Value ?? t.value ?? '' } : { key: '', value: String(t) })
      : [];

    return {
      id: allocationId, allocationId, accountId, publicIp, status, statusClass, statusOrder,
      associatedWith, region,
      idleDays: item.IdleDays ?? item.idleDays ?? '—', tagsArr,
      cost: `$${current.toFixed(2)}`, costValue: current,
      recommendedCost: `$${recommended.toFixed(2)}`, recommendedCostValue: recommended, savingValue: saving,
      saving: formatSavingStr(saving),
      recommendationRemark, recType, recPillClass, recLabel,
      actionLabel: 'Assist with AI', actionClass: 'mini-btn ai-assist', checked: false,
    };
  });

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue, totalAddresses, rows = []) => {
  const current = parseCost(totalPriceValue?.currentCost) || rows.reduce((sum, r) => sum + r.costValue, 0);
  const saving = parseCost(totalPriceValue?.savingCost) || rows.reduce((sum, r) => sum + r.savingValue, 0);
  const recommended = Math.max(0, current - saving);
  const attachedCount = rows.filter((r) => r.status !== 'Unattached').length;
  const unattachedCount = rows.filter((r) => r.status === 'Unattached').length;
  const dailyAvg = current > 0 ? (current / 30).toFixed(2) : '0.00';
  const uniqueRegions = new Set(rows.map((r) => r.region));
  const regionCounts = {};
  rows.forEach((r) => { regionCounts[r.region] = (regionCounts[r.region] || 0) + 1; });
  const sortedRegions = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);
  return [
    { id: 1, title: 'Total Addresses', value: `${totalAddresses}`, subtitle: `${attachedCount} attached · ${unattachedCount} unattached`, type: 't1' },
    { id: 2, title: `Total Cost (${new Date().toLocaleString('en-US', { month: 'short' })} ${new Date().getFullYear().toString().slice(-2)})`, value: `$${current.toFixed(2)}`, subtitle: `$${dailyAvg} / day average`, type: 't2' },
    { id: 3, title: 'Recommended Saving Cost', value: `$${saving.toFixed(2)}`, subtitle: `${unattachedCount} IPs · pure savings`, type: 't3' },
    // { id: 4, title: 'Regions in use', value: `${uniqueRegions.size}`, subtitle: sortedRegions.length > 0 ? `${sortedRegions[0][0]} leads with ${sortedRegions[0][1]}` : 'None', type: 't4' },
    { id: 4, title: 'Total Payable (after savings)', value: `$${recommended.toFixed(2)}`, subtitle: 'Estimated cost after applying recommendations', type: 't4' },
  ];
};

// ── Chart Builders ─────────────────────────────────────────

// Horizontal stacked bar: EIPs per region, sorted by total count descending
// ── Region Utilization Scorecard ───────────────────────────
const buildRegionStats = (rows) => {
  if (!rows.length) return [];
  const map = {};
  rows.forEach((r) => {
    if (!map[r.region]) map[r.region] = { attached: 0, unattached: 0, cost: 0 };
    if (r.status === 'Unattached') map[r.region].unattached++;
    else map[r.region].attached++;
    map[r.region].cost += r.costValue || 0;
  });
  return Object.entries(map)
    .map(([region, v]) => ({ region, ...v, total: v.attached + v.unattached, attachedPct: Math.round((v.attached / (v.attached + v.unattached)) * 100) }))
    .sort((a, b) => b.total - a.total);
};

const REGION_PALETTE = ['#2196f3','#2ea66f','#f59e0b','#ef4444','#9c27b0','#00bcd4','#ff5722','#607d8b','#e91e63','#3f51b5'];

const RegionDonutCard = ({ rows, loading }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const stats = buildRegionStats(rows);
  const totalCost = stats.reduce((s, r) => s + r.cost, 0);

  useEffect(() => {
    if (!stats.length || !canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: stats.map((s) => s.region),
        datasets: [{
          data: stats.map((s) => parseFloat(s.cost.toFixed(2))),
          backgroundColor: stats.map((_, i) => REGION_PALETTE[i % REGION_PALETTE.length]),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      options: {
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (c) => `$${c.parsed.toFixed(2)}/mo` } },
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
        <div style={{ width: 140, height: 140, borderRadius: '50%', background: 'linear-gradient(90deg,#f0f2f7 25%,#e2e6ef 50%,#f0f2f7 75%)', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[80,60,90,55,70].map((w, i) => <div key={i} style={{ height: 12, borderRadius: 4, background: '#f0f2f7', width: `${w}%` }} />)}
        </div>
      </div>
    );
  }
  if (!stats.length) return <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, paddingTop: 40 }}>No region data available</div>;

  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 6 }}>
      {/* Donut — fixed size, never grows */}
      <div style={{ position: 'relative', width: 148, height: 148, flexShrink: 0 }}>
        <canvas ref={canvasRef} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 0.3 }}>TOTAL</span>
          <strong style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.1 }}>${totalCost.toFixed(0)}</strong>
          <span style={{ fontSize: 9, color: 'var(--muted)' }}>/mo</span>
        </div>
      </div>

      {/* Compact legend — each row ~18px, scrolls only if truly massive */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, maxHeight: 200, overflowY: 'auto' }}>
        {stats.map(({ region, total, attached, unattached, attachedPct, cost }, i) => {
          const color = REGION_PALETTE[i % REGION_PALETTE.length];
          const barColor = attachedPct >= 90 ? '#2ea66f' : attachedPct >= 60 ? '#f59e0b' : '#ef4444';
          return (
            <div key={region} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text)', width: 86, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={region}>{region}</span>
              <span style={{ fontSize: 9.5, color: 'var(--muted)', flexShrink: 0, width: 38 }}>{total} IP{total !== 1 ? 's' : ''}</span>
              <div style={{ flex: 1, height: 5, background: '#eef1f7', borderRadius: 3, overflow: 'hidden', minWidth: 0 }}>
                <div style={{ width: `${attachedPct}%`, height: '100%', background: barColor, borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: barColor, width: 30, textAlign: 'right', flexShrink: 0 }}>{attachedPct}%</span>
              <span style={{ fontSize: 9.5, color: 'var(--muted)', width: 48, textAlign: 'right', flexShrink: 0 }}>${cost.toFixed(0)}/mo</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const buildByRegionChartData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => {
    if (!map[r.region]) map[r.region] = { attached: 0, unattached: 0 };
    if (r.status === 'Unattached') map[r.region].unattached++;
    else map[r.region].attached++;
  });
  const labels = Object.keys(map);
  if (!labels.length) return null;
  return { labels, datasets: [
    { label: 'Attached', data: labels.map((l) => map[l].attached), backgroundColor: '#2ea66f', borderRadius: 4, stack: 's' },
    { label: 'Unattached', data: labels.map((l) => map[l].unattached), backgroundColor: '#ef4444', borderRadius: 4, stack: 's' },
  ]};
};

const buildCostVsSavingsByRegionData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => {
    if (!map[r.region]) map[r.region] = { cost: 0, saving: 0 };
    map[r.region].cost += r.costValue;
    map[r.region].saving += r.savingValue;
  });
  const labels = Object.keys(map).sort((a, b) => map[b].cost - map[a].cost);
  if (!labels.length) return null;
  return { labels, datasets: [
    { label: 'Cost', data: labels.map((l) => parseFloat(map[l].cost.toFixed(2))), backgroundColor: '#2196f3', borderRadius: 4, barPercentage: 0.7 },
    { label: 'Savings', data: labels.map((l) => parseFloat(map[l].saving.toFixed(2))), backgroundColor: '#2ea66f', borderRadius: 4, barPercentage: 0.7 },
  ]};
};

const buildWasteChartData = (rows) => {
  const wasted = rows.filter((r) => r.status === 'Unattached').reduce((s, r) => s + r.costValue, 0);
  const productive = rows.filter((r) => r.status !== 'Unattached').reduce((s, r) => s + r.costValue, 0);
  if (wasted === 0 && productive === 0) return null;
  return {
    labels: ['Monthly spend'],
    datasets: [
      { label: 'Wasted (unattached)', data: [parseFloat(wasted.toFixed(2))], backgroundColor: '#ef4444', borderRadius: 4, barPercentage: 0.6 },
      { label: 'Productive (attached)', data: [parseFloat(productive.toFixed(2))], backgroundColor: '#2ea66f', borderRadius: 4, barPercentage: 0.6 },
    ],
  };
};

const buildInsightActions = (rows) => {
  const actions = [];
  const unattached = rows.filter((r) => r.status === 'Unattached');
  const stopped = rows.filter((r) => r.status === 'Stopped instance');
  const untagged = rows.filter((r) => !r.tagsArr || r.tagsArr.length === 0);
  let totalSaving = 0;
  if (unattached.length > 0) {
    const saving = unattached.reduce((sum, r) => sum + r.savingValue, 0);
    totalSaving += saving;
    actions.push({ id: 1, title: `Release ${unattached.length} unattached IPs`, amount: `$${saving.toFixed(2)}`, desc: 'No instance association > 14 days', actionLabel: 'Release →', actionClass: 'mini-btn', row: unattached[0] });
  }
  if (stopped.length > 0) {
    const saving = stopped.reduce((sum, r) => sum + r.savingValue, 0);
    totalSaving += saving;
    actions.push({ id: 2, title: 'Audit IPs attached to stopped instances', amount: `$${saving.toFixed(2)}`, desc: `${stopped.length} EIPs · still billed while instance stopped`, actionLabel: 'Review →', actionClass: 'mini-btn', row: stopped[0] });
  }
  if (untagged.length > 0)
    actions.push({ id: 3, title: `Tag all ${rows.length} EIPs (governance)`, amount: '—', desc: `${untagged.length} EIPs untagged · enables chargeback`, actionLabel: 'Auto-tag →', actionClass: 'mini-btn ghost' });
  return { actions, totalSaving };
};

// ── Main Component ──────────────────────────────────────────
const ElasticIPOptimization = () => {
  const { selectedAccount } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [kpiData, setKpiData] = useState([]);

  const [byRegionData, setByRegionData] = useState(null);
  const [regionCostData, setRegionCostData] = useState(null);
  const [wasteData, setWasteData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const { tagList } = useTagList('ELASTIC_IP');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const [regionFilter, setRegionFilter] = useState('All regions');
  const [statusFilter, setStatusFilter] = useState('All');
  const [ageFilter, setAgeFilter] = useState('All');

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const handleDetailsClick = (row) => {
    setAiData({ allocationId: row.allocationId, publicIp: row.publicIp, status: row.status, associatedWith: row.associatedWith, region: row.region, monthlyCost: row.cost, saving: row.saving });
    setAiPrompt('You are a cloud cost advisor. Based on this Elastic IP address information, give a short, friendly, and natural insight explaining why this IP is costing money, how they can release it, and the potential monthly savings.');
    setAiOpen(true);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: Array.from(value) }));
  };

  const fetchData = (isForceRefresh = false) => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setAllRows([]);
    OptimizationService.getTabularData(selectedAccount, 'CUSTOM_ELASTIC_IP', null, isForceRefresh)
      .then((data) => {
        const rows = mapEIPRows(data?.rows ?? []);
        setAllRows(rows);
        const totalPrices = { currentCost: rows.reduce((s, r) => s + r.costValue, 0), savingCost: rows.reduce((s, r) => s + r.savingValue, 0) };
        setKpiData(buildKpiData(data.totalPriceValue ?? totalPrices, rows.length, rows));
      })
      .catch(() => setKpiData(buildKpiData({}, 0, [])))
      .finally(() => setIsLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchData(); } }, [selectedAccount]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Elastic IP Optimization',
      kpi: kpiData,
      elasticIps: allRows,
    });
  }, [allRows, kpiData]);

  const filteredRows = allRows
    .filter((row) => {
      if (regionFilter !== 'All regions' && row.region !== regionFilter) return false;
      if (statusFilter === 'Attached' && row.status === 'Unattached') return false;
      if (statusFilter === 'Unattached' && row.status !== 'Unattached') return false;
      if (ageFilter !== 'All') {
        const days = parseInt(row.idleDays) || 0;
        if (ageFilter === '< 7 days' && days >= 7) return false;
        if (ageFilter === '7–30 days' && (days < 7 || days > 30)) return false;
        if (ageFilter === '> 30 days' && days <= 30) return false;
      }
      if (filterList.tag && filterList.values.length > 0) {
        const hasTag = (row.tagsArr ?? []).some(
          (t) => t.key === filterList.tag && filterList.values.includes(t.value)
        );
        if (!hasTag) return false;
      }
      return true;
    })
    .sort((a, b) => a.statusOrder - b.statusOrder || b.costValue - a.costValue);

  useEffect(() => {
    if (filteredRows.length > 0) {
      setByRegionData(buildByRegionChartData(filteredRows));
      setRegionCostData(buildCostVsSavingsByRegionData(filteredRows));
      setWasteData(buildWasteChartData(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setByRegionData(null); setRegionCostData(null); setWasteData(null);
      setInsightActions([]); setInsightTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, regionFilter, statusFilter, ageFilter]);

  const selectedCount = filteredRows.filter((r) => r.checked).length;
  const selectedSaving = filteredRows.filter((r) => r.checked).reduce((sum, r) => sum + (r.savingValue || 0), 0);
  const allChecked = filteredRows.length > 0 && filteredRows.every((r) => r.checked);

  const toggleRowChecked = (id) => setAllRows((prev) => prev.map((r) => r.id === id ? { ...r, checked: !r.checked } : r));
  const toggleAllChecked = () => { const v = !allChecked; setAllRows((prev) => prev.map((r) => ({ ...r, checked: v }))); };

  const eipColumns = [
    { key: 'checkbox', label: <span className={`checkbox ${allChecked ? 'checked' : ''}`} onClick={toggleAllChecked} />, className: 'center', render: (row) => <span className={`checkbox ${row.checked ? 'checked' : ''}`} onClick={(e) => { e.stopPropagation(); toggleRowChecked(row.id); }} /> },
    { key: 'allocationId', label: 'Allocation ID', render: (row) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.allocationId}</span> },
    { key: 'accountId', label: 'Account ID' },
    { key: 'publicIp', label: 'Public IP' },
    { key: 'status', label: 'Status', render: (row) => <span className={`status-pill ${row.statusClass}`}>{row.status}</span> },
    { key: 'associatedWith', label: 'Associated With' },
    { key: 'region', label: 'Region' },
    // { key: 'idleDays', label: 'Idle Days', className: 'num' },
    {
      key: 'tagsArr', label: 'Tags',
      render: (row) => row.tagsArr?.length > 0
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {row.tagsArr.map((t, i) => (
              <span key={i} style={{ fontSize: '11px', color: 'var(--text)' }}>
                <span style={{ color: 'var(--muted)', fontWeight: 600 }}>{t.key}:</span> {t.value}
              </span>
            ))}
          </div>
        : <span style={{ color: 'var(--muted)' }}>—</span>,
    },
    { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <span className="td-cost-bold">{row.cost}</span> },
    { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
     { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
    { key: 'action', label: 'Action', render: (row) => <button className={row.actionClass} onClick={() => handleDetailsClick(row)}>{row.actionLabel}</button> },
  ];

  const handleExport = () =>
    exportCSV(filteredRows, eipColumns.filter((c) => typeof c.label === 'string' && !['checkbox', 'action'].includes(c.key)), buildExportFilename('elastic_ip_optimization', selectedAccount));

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Elastic IP</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Elastic IP Optimization{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{filteredRows.length} addresses</span>}
          </h1>
          <div className="meta">Identify and release unattached IPs · billed at $0.005/hr each</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={filteredRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchData(true)}>↻ Refresh</button>
          {/* <button className="btn primary">⚡ Release All Unattached</button> */}
        </div>
      </div>

      <KpiRow kpiData={kpiData} loading={isLoading} />

      <div className="grid-3">
        {/* Region utilization donut — cost by region + compact utilization legend */}
        <Card className="first" title="Region utilization" subtitle="Cost share · bar = % attached · green ≥90% · orange ≥60% · red <60%">
          <RegionDonutCard rows={filteredRows} loading={isLoading} />
        </Card>
        <Card className="first" title="Addresses by region" subtitle="Attached vs unattached per region">
          <VerticalBarChart data={byRegionData} loading={isLoading} stacked={true} />
        </Card>
        <TopActionsCard title="Top EIP actions" actions={insightActions} total={insightTotal} loading={isLoading} entityName="IPs" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        {/* <div className="field">
          <label>Region</label>
          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
            <option value="All regions">All regions</option>
            {[...new Set(allRows.map((r) => r.region).filter(Boolean))].sort().map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div> */}
        {/* <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All</option>
            <option value="Attached">Attached</option>
            <option value="Unattached">Unattached</option>
          </select>
        </div> */}
        <div className="spacer" />
        <button className="btn ghost" onClick={() => { setFilterList({ tag: '', values: [] }); setRegionFilter('All regions'); setStatusFilter('All'); }}>✕ Clear</button>
        <button className="btn primary" onClick={() => fetchData(true)}>↻ Refresh</button>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-bar">
          <span><strong>{selectedCount} IPs selected</strong> · ${selectedSaving.toFixed(2)}/mo combined savings</span>
          <button className="btn ghost">🏷 Tag</button>
          <button className="btn primary">🗑 Release</button>
        </div>
      )}

      <Table
        columns={eipColumns}
        rows={filteredRows}
        loading={isLoading}
        defaultPageSize={25}
        showToolbar={true}
        onExport={handleExport}
        defaultSortKey="statusOrder"
        defaultSortDir="asc"
        emptyText="No Elastic IPs found."
        subscriptionGated
      />

      {/* Cost Waste Leaderboard — commented out: graph data not yet available */}
      <div className="grid-2 grid-gap-top">
        <Card className="first" title="Cost vs savings by region" subtitle="Grouped bar — current cost vs potential savings per region">
          <VerticalBarChart data={regionCostData} loading={isLoading} grouped={true} yAxisPrefix="$" yAxisSuffix="" />
        </Card>
        <Card className="first" title="Wasted vs productive spend" subtitle={`Unattached waste ($${filteredRows.filter((r) => r.status === 'Unattached').reduce((s, r) => s + r.costValue, 0).toFixed(2)}) vs attached ($${filteredRows.filter((r) => r.status !== 'Unattached').reduce((s, r) => s + r.costValue, 0).toFixed(2)})`}>
          <HorizontalBarChart data={wasteData} loading={isLoading} stacked={true} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default ElasticIPOptimization;
